import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import { Icon, type IconName } from './icon';
import { cn } from '@/lib/cn';

export type BadgeTone = 'neutral' | 'brand' | 'warm' | 'ok' | 'warn' | 'err' | 'ink' | 'outline';
export type BadgeSize = 'sm' | 'md' | 'lg';

const TONE: Readonly<Record<BadgeTone, string>> = {
  neutral: 'neutral bg-[var(--bg-soft)] text-[var(--ink-2)]',
  brand: 'brand bg-[var(--brand-wash)] text-[var(--brand-ink)]',
  warm: 'warm bg-[var(--warm-wash)] text-[var(--warm)]',
  ok: 'ok bg-[var(--ok-wash)] text-[var(--ok)]',
  warn: 'warn bg-[var(--warn-wash)] text-[var(--warn)]',
  err: 'err bg-[var(--err-wash)] text-[var(--err)]',
  ink: 'ink bg-[var(--ink)] text-[var(--bg-surface)]',
  outline: 'outline border border-[var(--rule-strong)] text-[var(--ink-2)]',
};
const SIZE: Readonly<Record<BadgeSize, string>> = {
  sm: 'h-5 px-1.5 text-[10px] gap-1',
  md: 'h-6 px-2 text-[11px] gap-1',
  lg: 'h-7 px-2.5 text-[12px] gap-1.5',
};

export type BadgeProps = Omit<HTMLAttributes<HTMLSpanElement>, 'children'> & {
  tone?: BadgeTone;
  size?: BadgeSize;
  icon?: IconName;
  children?: ReactNode;
};

export function Badge({
  tone = 'neutral', size = 'md', icon, className, children, ...rest
}: BadgeProps): ReactElement {
  const iconSize = size === 'sm' ? 10 : 12;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-[var(--r-sm)] font-semibold uppercase tracking-wider whitespace-nowrap',
        TONE[tone],
        SIZE[size],
        className,
      )}
      {...rest}
    >
      {icon && <Icon name={icon} size={iconSize} />}
      {children}
    </span>
  );
}
